import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Save, Clock, Calendar, Timer } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { updateNode } from '@/services/api';
import type { FlowNode } from '@/types/flow';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

interface DelayConfigProps {
    node: FlowNode;
    onClose: () => void;
}

type TimeUnit = 'seconds' | 'minutes' | 'hours' | 'days';

export default function DelayConfig({ node, onClose }: DelayConfigProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const getInitialTimeUnit = (seconds: number): TimeUnit => {
        if (seconds >= 86400 && seconds % 86400 === 0) return 'days';
        if (seconds >= 3600 && seconds % 3600 === 0) return 'hours';
        if (seconds >= 60 && seconds % 60 === 0) return 'minutes';
        return 'seconds';
    };

    const getInitialTimeValue = (seconds: number, unit: TimeUnit): number => {
        switch (unit) {
            case 'days': return seconds / 86400;
            case 'hours': return seconds / 3600;
            case 'minutes': return seconds / 60;
            default: return seconds;
        }
    };

    const initialSeconds = node.data.config?.delay_seconds || 60;
    const initialUnit = getInitialTimeUnit(initialSeconds);

    const [nodeName, setNodeName] = useState(node.data.node_name || '');
    const [timeValue, setTimeValue] = useState(getInitialTimeValue(initialSeconds, initialUnit));
    const [timeUnit, setTimeUnit] = useState<TimeUnit>(initialUnit);

    const calculateSeconds = (value: number, unit: TimeUnit): number => {
        switch (unit) {
            case 'seconds': return value;
            case 'minutes': return value * 60;
            case 'hours': return value * 3600;
            case 'days': return value * 86400;
            default: return value;
        }
    };

    const totalSeconds = calculateSeconds(timeValue, timeUnit);

    const getTimeUnitInfo = (unit: TimeUnit) => {
        switch (unit) {
            case 'seconds':
                return { label: 'Segundos', icon: Timer, min: 1, max: 59 };
            case 'minutes':
                return { label: 'Minutos', icon: Clock, min: 1, max: 59 };
            case 'hours':
                return { label: 'Horas', icon: Clock, min: 1, max: 23 };
            case 'days':
                return { label: 'Dias', icon: Calendar, min: 1, max: 365 };
        }
    };

    const formatDuration = (seconds: number): string => {
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        const parts = [];
        if (days > 0) parts.push(`${days}d`);
        if (hours > 0) parts.push(`${hours}h`);
        if (minutes > 0) parts.push(`${minutes}m`);
        if (secs > 0) parts.push(`${secs}s`);

        return parts.join(' ') || '0s';
    };

    const saveMutation = useMutation({
        mutationFn: () => updateNode(node.data.id, {
            node_name: nodeName,
            config: { delay_seconds: totalSeconds }
        }),
        onSuccess: () => {
            toast({ title: 'Delay salvo!', description: 'Configurações salvas com sucesso.' });
            queryClient.invalidateQueries({ queryKey: ['funnel-details'] });
            onClose();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    const currentUnitInfo = getTimeUnitInfo(timeUnit);
    const Icon = currentUnitInfo.icon;

    return (
        <>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Nome */}
                <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">Nome do Delay</Label>
                    <Input
                        value={nodeName}
                        onChange={(e) => setNodeName(e.target.value)}
                        placeholder="Ex: Aguardar resposta"
                        className="h-10"
                    />
                </div>

                {/* Tempo de Espera */}
                <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">Tempo de Espera</Label>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label className="text-xs text-gray-500">Valor</Label>
                            <Input
                                type="number"
                                min={currentUnitInfo.min}
                                max={currentUnitInfo.max}
                                value={timeValue}
                                onChange={(e) => setTimeValue(Number(e.target.value))}
                                className="h-10 text-lg font-semibold"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs text-gray-500">Unidade</Label>
                            <Select
                                value={timeUnit}
                                onValueChange={(value: TimeUnit) => {
                                    setTimeUnit(value);
                                    const newInfo = getTimeUnitInfo(value);
                                    if (timeValue < newInfo.min) setTimeValue(newInfo.min);
                                    if (timeValue > newInfo.max) setTimeValue(newInfo.max);
                                }}
                            >
                                <SelectTrigger className="h-10">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="seconds">
                                        <div className="flex items-center gap-2">
                                            <Timer className="w-4 h-4" />
                                            <span>Segundos</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="minutes">
                                        <div className="flex items-center gap-2">
                                            <Clock className="w-4 h-4" />
                                            <span>Minutos</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="hours">
                                        <div className="flex items-center gap-2">
                                            <Clock className="w-4 h-4" />
                                            <span>Horas</span>
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="days">
                                        <div className="flex items-center gap-2">
                                            <Calendar className="w-4 h-4" />
                                            <span>Dias</span>
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Preview do Tempo */}
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-orange-100 rounded-lg">
                                <Icon className="w-5 h-5 text-orange-600" />
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-medium text-orange-900 mb-1">
                                    Tempo Total de Espera
                                </p>
                                <p className="text-2xl font-bold text-orange-600">
                                    {timeValue} {currentUnitInfo.label.toLowerCase()}
                                </p>
                                <p className="text-xs text-orange-700 mt-1">
                                    = {formatDuration(totalSeconds)} ({totalSeconds.toLocaleString()} segundos)
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Info Helper */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-900">
                        💡 <strong>Dica:</strong> O fluxo pausará por {timeValue} {currentUnitInfo.label.toLowerCase()} antes de prosseguir para o próximo passo.
                    </p>
                </div>

                {/* Sugestões Rápidas */}
                <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Sugestões Rápidas</Label>
                    <div className="grid grid-cols-2 gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setTimeValue(5);
                                setTimeUnit('minutes');
                            }}
                            className="h-9 text-xs"
                        >
                            <Clock className="w-3 h-3 mr-1" />
                            5 minutos
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setTimeValue(1);
                                setTimeUnit('hours');
                            }}
                            className="h-9 text-xs"
                        >
                            <Clock className="w-3 h-3 mr-1" />
                            1 hora
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setTimeValue(24);
                                setTimeUnit('hours');
                            }}
                            className="h-9 text-xs"
                        >
                            <Calendar className="w-3 h-3 mr-1" />
                            24 horas
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setTimeValue(7);
                                setTimeUnit('days');
                            }}
                            className="h-9 text-xs"
                        >
                            <Calendar className="w-3 h-3 mr-1" />
                            7 dias
                        </Button>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="border-t bg-white p-6 flex-shrink-0">
                <Button
                    onClick={() => saveMutation.mutate()}
                    disabled={saveMutation.isPending || timeValue < currentUnitInfo.min}
                    className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white font-medium"
                >
                    <Save className="w-4 h-4 mr-2" />
                    {saveMutation.isPending ? 'Salvando...' : 'Salvar Configurações'}
                </Button>
            </div>
        </>
    );
}